/**
 * Branding configuration — sourced entirely from VITE_ environment variables.
 *
 * Usage:
 *   import { brand } from '../../lib/branding';
 *   brand.businessName  // "ISATS"
 *   brand.colors.primary // "#2563eb"
 *
 * To create a new brand/domain:
 *   1. Copy .env.local → .env.{brand-name}
 *   2. Change all VITE_BRAND_* and VITE_FEATURE_* and VITE_ROLE_* values
 *   3. Build: VITE_BRAND={brand-name} npm run build
 *   4. Deploy the resulting image to the new domain
 */

export interface BrandColors {
  primary: string;
  primaryLight: string;
  primaryDark: string;
  accent: string;
  /** Text color on dark brand backgrounds (login left panel, email banners) */
  textLight?: string;
}

export interface BrandLinks {
  termsUrl: string;
  privacyUrl: string;
  copyright: string;
}

export interface BrandFeatures {
  jobBoard: boolean;
  events: boolean;
  sponsorPortal: boolean;
}

export interface BrandRoleSetup {
  sponsor: boolean;
  employer: boolean;
  referrer: boolean;
}

export interface BrandConfig {
  /** Business / organization name */
  businessName: string;
  /** Short tagline shown on the login page */
  tagline: string;
  /** Email address users see for support */
  supportEmail: string;
  /** Brand color palette */
  colors: BrandColors;
  /** URL to brand logo (light background) */
  logoUrl: string;
  /** URL to favicon (overrides index.html default) */
  faviconUrl: string;
  /** Legal / footer links */
  links: BrandLinks;
  /** Feature toggles */
  features: BrandFeatures;
  /**
   * LEGACY static role visibility (build-time VITE_ROLE_* flags). NOT the
   * signup gate: which roles may self-register is a per-workspace runtime
   * decision (the admin "Signup Roles" toggles → workspace `signupRoles`,
   * enforced server-side by validateWorkspaceForSignup and surfaced via
   * GET /api/auth/signup-roles). A single build serves many workspaces, so
   * a compiled-in flag cannot express that policy — these values are used
   * only as the signup screen's fallback until the workspace set loads
   * (see resolveSignupTileRoles in lib/onboarding).
   */
  roleSetup: BrandRoleSetup;
}

function bool(val: string | undefined): boolean {
  return val === 'true' || val === '1' || val === 'yes';
}

/**
 * Reads all VITE_BRAND_*, VITE_FEATURE_*, and VITE_ROLE_* env vars
 * and returns a typed BrandConfig object.
 */
export function loadBrandConfig(): BrandConfig {
  const env = import.meta.env;

  return {
    // ── Identity ────────────────────────────────────────
    businessName: (env.VITE_BRAND_BUSINESS_NAME as string) || 'ISATS',
    tagline: (env.VITE_BRAND_TAGLINE as string) || 'International Student & Training Services',
    supportEmail: (env.VITE_BRAND_SUPPORT_EMAIL as string) || 'apply@isats.org',

    // ── Colors ───────────────────────────────────────────
    colors: {
      primary: (env.VITE_BRAND_COLOR_PRIMARY as string) || '#2563eb',
      primaryLight: (env.VITE_BRAND_COLOR_PRIMARY_LIGHT as string) || '#dbeafe',
      primaryDark: (env.VITE_BRAND_COLOR_PRIMARY_DARK as string) || '#1d4ed8',
      accent: (env.VITE_BRAND_COLOR_ACCENT as string) || '#059669',
      textLight: (env.VITE_BRAND_COLOR_TEXT_LIGHT as string) || undefined,
    },

    // ── Assets ───────────────────────────────────────────
    logoUrl: (env.VITE_BRAND_LOGO_URL as string) || '',
    faviconUrl: (env.VITE_BRAND_FAVICON_URL as string) || '',

    // ── Links ────────────────────────────────────────────
    links: {
      termsUrl: (env.VITE_BRAND_TERMS_URL as string) || 'https://isats.org/terms',
      privacyUrl: (env.VITE_BRAND_PRIVACY_URL as string) || 'https://isats.org/privacy',
      copyright: (env.VITE_BRAND_COPYRIGHT as string) || '© 2026 ISATS. All rights reserved.',
    },

    // ── Features ─────────────────────────────────────────
    features: {
      jobBoard: bool(env.VITE_FEATURE_JOB_BOARD as string) || true,
      events: bool(env.VITE_FEATURE_EVENTS as string) || true,
      sponsorPortal: bool(env.VITE_FEATURE_SPONSOR_PORTAL as string) || false,
    },

    // ── Role signup visibility ───────────────────────────
    roleSetup: {
      sponsor: bool(env.VITE_ROLE_SPONSOR_USER_ACCOUNT_SETUP as string) || false,
      employer: bool(env.VITE_ROLE_EMPLOYER_USER_ACCOUNT_SETUP as string) || true,
      referrer: bool(env.VITE_ROLE_REFERRER_USER_ACCOUNT_SETUP as string) || true,
    },
  };
}

/**
 * Singleton brand instance — safe to import anywhere after app boot.
 */
export const brand: BrandConfig = loadBrandConfig();

/**
 * Applies brand config to the DOM:
 *   - Sets CSS custom properties on :root
 *   - Updates <title>
 *   - Updates favicon <link>
 *   - Updates meta description
 *
 * Call once at app startup (e.g. in main.tsx or App.tsx).
 */
export function applyBrandToDOM(cfg: BrandConfig = brand): void {
  const root = document.documentElement;

  // ── CSS Variables ──────────────────────────────────────
  root.style.setProperty('--brand-primary', cfg.colors.primary);
  root.style.setProperty('--brand-primary-light', cfg.colors.primaryLight);
  root.style.setProperty('--brand-primary-dark', cfg.colors.primaryDark);
  root.style.setProperty('--brand-accent', cfg.colors.accent);
  if (cfg.colors.textLight) root.style.setProperty('--brand-text-light', cfg.colors.textLight);

  // ── Tailwind blue scale + info tokens follow the brand ──
  // The premium design system still uses hardcoded blue-* utilities across the
  // UI. Tailwind emits those as var(--color-blue-*) with a color-mix() override,
  // so overriding the tokens here (inline > stylesheet) keeps every blue accent
  // on-brand for any tenant. color-mix() strings resolve at runtime.
  const primary = cfg.colors.primary;
  const light = cfg.colors.primaryLight;
  const dark = cfg.colors.primaryDark;
  const mix = (a: string, w: number, b: string) => `color-mix(in srgb, ${a} ${Math.round(w * 100)}%, ${b})`;
  const blueScale: Record<string, string> = {
    '--color-blue-50': mix(light, 0.6, '#ffffff'),
    '--color-blue-100': light,
    '--color-blue-200': mix(primary, 0.18, '#ffffff'),
    '--color-blue-300': mix(primary, 0.34, '#ffffff'),
    '--color-blue-400': mix(primary, 0.58, '#ffffff'),
    '--color-blue-500': mix(primary, 0.82, '#ffffff'),
    '--color-blue-600': primary,
    '--color-blue-700': dark,
    '--color-blue-800': mix(dark, 0.84, '#000000'),
    '--color-blue-900': mix(dark, 0.68, '#000000'),
    '--color-blue-950': mix(dark, 0.52, '#000000'),
  };
  for (const [name, value] of Object.entries(blueScale)) {
    root.style.setProperty(name, value);
  }
  root.style.setProperty('--color-info', primary);
  root.style.setProperty('--color-info-bg', light);
  root.style.setProperty('--color-info-border', mix(primary, 0.28, '#ffffff'));

  // ── Document title ────────────────────────────────────
  document.title = cfg.businessName + ' Portal';

  // ── Meta description ──────────────────────────────────
  let metaDesc = document.querySelector('meta[name="description"]');
  if (!metaDesc) {
    metaDesc = document.createElement('meta');
    metaDesc.setAttribute('name', 'description');
    document.head.appendChild(metaDesc);
  }
  metaDesc.setAttribute('content', cfg.tagline);

  // ── Favicon ────────────────────────────────────────────
  if (cfg.faviconUrl) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = cfg.faviconUrl;
  }
}
